import { computed, nextTick, ref, type ComputedRef } from 'vue';

import type { useCommonStore } from '@/stores/common';
import type { useUserStore } from '@/stores/user';

import type { HelperCampaignViewModel } from '../types';

type CommonStore = ReturnType<typeof useCommonStore>;
type UserStore = ReturnType<typeof useUserStore>;

export function useHelperSignupFlow(params: {
  campaign: ComputedRef<HelperCampaignViewModel>;
  hasStrongCriteriaSelected: ComputedRef<boolean>;
  buildUserCriteriaPayload: () => Array<{
    criteriaId: number;
    answerKey?: string;
    answerKeys?: string[];
  }>;
  commonStore: CommonStore;
  userStore: UserStore;
  t: (key: string) => string;
  emit: {
    (event: 'close'): void;
    (event: 'notify'): void;
  };
}) {
  const consentSent = ref(false);
  const formSectionRef = ref<HTMLElement | null>(null);

  const primaryButtonText = computed(() => {
    if (consentSent.value) {
      return params.t('generic.confirm');
    }
    return params.t('helperCampaignSignUpView.enrollAsHelper');
  });

  const isPrimaryDisabled = computed(() => {
    if (!consentSent.value) {
      return false;
    }
    return !params.hasStrongCriteriaSelected.value;
  });

  const goToFormSection = async () => {
    await nextTick();
    if (formSectionRef.value) {
      formSectionRef.value.scrollIntoView({ inline: 'start' });
    }
  };

  const completeJoinFlow = async () => {
    window.scrollTo(0, 0);
    await nextTick();

    params.emit('close');
    params.emit('notify');

    params.commonStore.setSlideOverModal({
      open: true,
    });
  };

  const submitJoin = async () => {
    const campaignId = params.campaign.value.id;
    if (!campaignId) {
      return;
    }

    try {
      const userCriteria = params.buildUserCriteriaPayload();

      await params.userStore.joinCampaign({
        campaignId,
        userCriteria,
      });

      await completeJoinFlow();
    } catch (error) {
      console.log(error);
    }
  };

  const onPrimaryAction = async () => {
    if (!consentSent.value) {
      consentSent.value = true;
      await goToFormSection();
      return;
    }

    await submitJoin();
  };

  return {
    consentSent,
    formSectionRef,
    primaryButtonText,
    isPrimaryDisabled,
    onPrimaryAction,
  };
}
